import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import React from "react";
import { cn } from "@/lib/utils/cn";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger" | "provider" | "link" | "warning" | "neutral" | "white" | "success";
  size?: "sm" | "md" | "responsive";
  icon?: LucideIcon;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  iconOnly?: boolean;
  loading?: boolean;
  children?: React.ReactNode;
}

const variantStyles = {
  primary:
    "bg-navigator-primary text-white hover:bg-navigator-primary-dark active:bg-navigator-primary-darker cursor-pointer border border-transparent shadow-sm",
  secondary:
    "bg-navigator-secondary text-navigator-text-primary hover:bg-navigator-secondary-dark active:bg-navigator-secondary-darker cursor-pointer border border-transparent shadow-sm",
  outline:
    "border border-navigator-primary text-navigator-primary hover:bg-navigator-primary hover:text-white active:bg-navigator-primary-darker active:text-white cursor-pointer shadow-sm",
  ghost:
    "text-navigator-primary hover:bg-gray-100 active:bg-gray-200 cursor-pointer border border-transparent",
  danger:
    "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 cursor-pointer border border-transparent shadow-sm",
  provider:
    "bg-white text-navigator-text-primary border border-gray-200 hover:bg-gray-50 active:bg-gray-100 cursor-pointer shadow-sm",
  link:
    "text-navigator-primary hover:underline cursor-pointer border-0 bg-transparent p-0",
  warning:
    "bg-amber-200 text-amber-900 hover:bg-amber-300 active:bg-amber-400 cursor-pointer border border-transparent shadow-sm",
  neutral:
    "bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400 cursor-pointer border border-transparent shadow-sm",
  white:
    "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 cursor-pointer shadow-sm",
  success:
    "bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 cursor-pointer border border-transparent shadow-sm",
};

const sizeStyles = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-1.5 text-base",
  responsive:
    "px-3 py-1.5 text-xs " +
    "min-[1920px]:px-4 min-[1920px]:py-1.5 min-[1920px]:text-base",
};

const iconOnlySizeStyles = {
  sm: "p-1.5",
  md: "p-1.5",
  responsive: "p-1 min-[1920px]:p-1.5",
};

const iconSizes = {
  sm: "w-4 h-4",
  md: "w-4 h-4",
  responsive: "w-3.5 h-3.5 min-[1920px]:w-4 min-[1920px]:h-4",
};

export function Button({
  variant = "primary",
  size = "md",
  icon: Icon,
  startIcon,
  endIcon,
  fullWidth,
  iconOnly,
  loading = false,
  children,
  className = "",
  disabled,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const variantClass = variantStyles[variant];
  const sizeClass = variant === "link" ? "" : iconOnly ? iconOnlySizeStyles[size] : sizeStyles[size];

  const resolvedStartIcon = loading
    ? <Loader2 className={cn(iconSizes[size], "animate-spin")} />
    : startIcon;

  return (
    <button
      ref={ref}
      className={cn(
        variantClass,
        sizeClass,
        "group inline-flex items-center gap-2 whitespace-nowrap",
        "rounded-lg font-medium transition-all duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
        fullWidth && "w-full justify-center",
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {resolvedStartIcon}
      {Icon && !resolvedStartIcon && !loading && <Icon className={iconSizes[size]} />}
      {!iconOnly && children}
      {endIcon}
    </button>
  );
}

Button.displayName = "Button";
